// Merge Athletes — admin tool for cleaning up duplicate athlete records.
// Common case: junior athlete registered twice with different parent emails.
// Workflow: pick primary + duplicate → preview impact → confirm.
//
// The CF `mergeAthletes` does all the heavy lifting in a single transaction:
// re-points attendance/purchases/registrations, merges fields, archives the
// duplicate. See functions/index.js for the server logic.

import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Layout from '../components/Layout';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, Alert,
  CircularProgress, Stack, RadioGroup, Radio, FormControlLabel, FormLabel,
  Divider, Chip,
} from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const DARK = '#1E3A5F';

function MergeAthletes() {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [primary, setPrimary] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [chosenParent, setChosenParent] = useState('');
  const [counts, setCounts] = useState(null); // { attendance, purchases, registrations }
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [merging, setMerging] = useState(false);
  const [success, setSuccess] = useState(null); // result obj
  const [error, setError] = useState('');
  const [parentDisplay, setParentDisplay] = useState({}); // uid → name

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'athletes'));
        const data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          // Hide already-merged docs from the pickers
          .filter(a => !a.mergedInto)
          .sort((a, b) => `${a.lastName || ''} ${a.firstName || ''}`.localeCompare(`${b.lastName || ''} ${b.firstName || ''}`));
        setAthletes(data);
      } catch (e) {
        console.error('Failed to load athletes:', e);
        setError('Failed to load athletes.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Look up parent names for display whenever primary/duplicate change
  useEffect(() => {
    const idsToLookUp = [primary?.parentUserId, duplicate?.parentUserId].filter(Boolean)
      .filter(id => !parentDisplay[id]);
    if (idsToLookUp.length === 0) return;
    (async () => {
      const updates = {};
      await Promise.all(idsToLookUp.map(async (id) => {
        try {
          const snap = await getDocs(query(collection(db, 'users'), where('uid', '==', id), limit(1)));
          if (!snap.empty) {
            const u = snap.docs[0].data();
            updates[id] = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || id.slice(0, 8);
          } else {
            // Try doc id lookup as fallback
            const docSnap = await getDocs(query(collection(db, 'users'), where('email', '==', id)));
            updates[id] = docSnap.empty ? id.slice(0, 8) : (docSnap.docs[0].data().firstName || id.slice(0, 8));
          }
        } catch {
          updates[id] = id.slice(0, 8);
        }
      }));
      setParentDisplay(prev => ({ ...prev, ...updates }));
    })();
  }, [primary, duplicate]);

  // Default chosen parent when both athletes are picked
  useEffect(() => {
    if (!primary || !duplicate) { setChosenParent(''); return; }
    if (primary.parentUserId === duplicate.parentUserId) {
      setChosenParent(primary.parentUserId || '');
    } else if (primary.parentUserId) {
      setChosenParent(primary.parentUserId);
    } else if (duplicate.parentUserId) {
      setChosenParent(duplicate.parentUserId);
    } else {
      setChosenParent('');
    }
  }, [primary, duplicate]);

  // Live counts of records that'll move when both picked
  useEffect(() => {
    if (!duplicate) { setCounts(null); return; }
    setLoadingCounts(true);
    (async () => {
      try {
        const [attSnap, purSnap, regSnap] = await Promise.all([
          getDocs(query(collection(db, 'attendance'), where('athleteId', '==', duplicate.id))),
          getDocs(query(collection(db, 'purchases'), where('athleteId', '==', duplicate.id))),
          getDocs(query(collection(db, 'registrations'), where('athlete_id', '==', duplicate.id))),
        ]);
        setCounts({ attendance: attSnap.size, purchases: purSnap.size, registrations: regSnap.size });
      } catch (e) {
        console.error('Count load failed:', e);
        setCounts(null);
      } finally {
        setLoadingCounts(false);
      }
    })();
  }, [duplicate]);

  function fmtAthleteOption(a) {
    if (!a) return '';
    const sb = typeof a.sessionBalance === 'number' ? ` · ${a.sessionBalance} sessions` : '';
    return `${a.firstName || ''} ${a.lastName || ''}${sb}${a.email ? ` · ${a.email}` : ''}${a.athleteType ? ` · ${a.athleteType}` : ''}`;
  }

  function fmtTs(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  }

  async function handleMerge() {
    if (!primary || !duplicate) return;
    setMerging(true);
    setError('');
    try {
      const fns = getFunctions();
      const mergeAthletes = httpsCallable(fns, 'mergeAthletes');
      const result = await mergeAthletes({
        primaryId: primary.id,
        duplicateId: duplicate.id,
        parentUserId: chosenParent || null,
      });
      setSuccess(result.data);
      // Reset
      setPrimary(null); setDuplicate(null); setCounts(null);
    } catch (e) {
      console.error('Merge failed:', e);
      setError(e.message || 'Merge failed.');
    } finally {
      setMerging(false);
    }
  }

  const sameDoc = primary && duplicate && primary.id === duplicate.id;
  const canMerge = primary && duplicate && !sameDoc && !merging;
  const parentsMatch = primary && duplicate && primary.parentUserId === duplicate.parentUserId;

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <MergeTypeIcon sx={{ color: DARK, fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: DARK }}>Merge Athletes</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Combine two athlete records into one. Attendance, purchases, and tournament registrations
          re-point to the primary. The duplicate is archived (not deleted) and can be restored from Firestore if needed.
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            <strong>Merge complete.</strong> {success.counts.attendance} attendance, {success.counts.purchases} purchase{success.counts.purchases === 1 ? '' : 's'},
            {' '}{success.counts.registrations} registration{success.counts.registrations === 1 ? '' : 's'} re-pointed to the primary athlete.
            Duplicate has been archived.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Autocomplete
              options={athletes}
              getOptionLabel={fmtAthleteOption}
              value={primary}
              onChange={(_, v) => setPrimary(v)}
              loading={loading}
              renderInput={(params) => (
                <TextField {...params} label="Primary athlete (the one to keep)" placeholder="Search by name…" />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
            <Autocomplete
              options={athletes.filter(a => !primary || a.id !== primary.id)}
              getOptionLabel={fmtAthleteOption}
              value={duplicate}
              onChange={(_, v) => setDuplicate(v)}
              loading={loading}
              renderInput={(params) => (
                <TextField {...params} label="Duplicate athlete (will be archived)" placeholder="Search by name…" />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
          </Stack>
        </Paper>

        {/* Preview panel */}
        {primary && duplicate && (
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mb: 3 }}>
            {sameDoc && (
              <Alert severity="error" sx={{ mb: 2 }}>Primary and duplicate are the same athlete. Pick a different duplicate.</Alert>
            )}

            <Typography variant="h6" sx={{ fontWeight: 700, color: DARK, mb: 2 }}>Preview</Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
              {[
                { label: 'Primary (kept)', a: primary, color: '#27AE60' },
                { label: 'Duplicate (archived)', a: duplicate, color: '#E67E22' },
              ].map(({ label, a, color }) => (
                <Box key={label} sx={{ flex: 1, p: 2, borderRadius: 2, border: `2px solid ${color}`, background: `${color}08` }}>
                  <Chip label={label} size="small" sx={{ backgroundColor: color, color: 'white', fontWeight: 700, mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>{a.firstName} {a.lastName}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>ID: {a.id}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={0.5} sx={{ fontSize: '0.85rem' }}>
                    <div><strong>Email:</strong> {a.email || '—'}</div>
                    <div><strong>Phone:</strong> {a.phone || '—'}</div>
                    <div><strong>Parent UID:</strong> {a.parentUserId ? (parentDisplay[a.parentUserId] || a.parentUserId.slice(0, 8) + '…') : '—'}</div>
                    <div><strong>Type:</strong> {a.athleteType || '—'}</div>
                    <div><strong>Skill:</strong> {a.skillGroup || '—'}</div>
                    <div><strong>Sessions:</strong> {a.sessionBalance ?? 0}</div>
                    <div><strong>Last check-in:</strong> {fmtTs(a.lastCheckIn)}</div>
                    <div><strong>Birthday:</strong> {a.birthday || '—'}</div>
                  </Stack>
                </Box>
              ))}
            </Stack>

            {/* Parent choice */}
            {!sameDoc && !parentsMatch && (primary.parentUserId || duplicate.parentUserId) && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                <strong>Different parents.</strong> Choose which parent the merged athlete should report to.
              </Alert>
            )}
            {!sameDoc && (primary.parentUserId || duplicate.parentUserId) && (
              <Box sx={{ mb: 2 }}>
                <FormLabel sx={{ fontWeight: 700, color: DARK, fontSize: '0.85rem' }}>Parent for merged athlete</FormLabel>
                <RadioGroup value={chosenParent} onChange={e => setChosenParent(e.target.value)}>
                  {primary.parentUserId && (
                    <FormControlLabel
                      value={primary.parentUserId}
                      control={<Radio size="small" />}
                      label={`Primary's parent: ${parentDisplay[primary.parentUserId] || primary.parentUserId.slice(0, 12) + '…'}`}
                    />
                  )}
                  {duplicate.parentUserId && duplicate.parentUserId !== primary.parentUserId && (
                    <FormControlLabel
                      value={duplicate.parentUserId}
                      control={<Radio size="small" />}
                      label={`Duplicate's parent: ${parentDisplay[duplicate.parentUserId] || duplicate.parentUserId.slice(0, 12) + '…'}`}
                    />
                  )}
                </RadioGroup>
              </Box>
            )}

            {/* Counts */}
            <Box sx={{ p: 2, background: '#F5F7FA', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>What will change</Typography>
              {loadingCounts ? (
                <CircularProgress size={20} />
              ) : counts ? (
                <Stack spacing={0.5} sx={{ fontSize: '0.88rem', color: '#444' }}>
                  <div>• <strong>{counts.attendance}</strong> attendance record{counts.attendance === 1 ? '' : 's'} re-point to primary</div>
                  <div>• <strong>{counts.purchases}</strong> purchase{counts.purchases === 1 ? '' : 's'} re-point to primary</div>
                  <div>• <strong>{counts.registrations}</strong> tournament registration{counts.registrations === 1 ? '' : 's'} re-point to primary</div>
                  <div>• Session balances added: <strong>{(primary.sessionBalance || 0) + (duplicate.sessionBalance || 0)}</strong> total</div>
                  <div>• Blank fields on primary will be filled from duplicate where available</div>
                  <div>• Duplicate athlete will be archived</div>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">Pick a duplicate to see counts.</Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={() => { setPrimary(null); setDuplicate(null); }} disabled={merging}>
                Reset
              </Button>
              <Button
                variant="contained" color="primary"
                disabled={!canMerge}
                onClick={handleMerge}
                startIcon={merging ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <MergeTypeIcon />}
              >
                {merging ? 'Merging…' : 'Confirm Merge'}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Help text */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px dashed #ccc', background: '#FAFBFC' }}>
          <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.6 }}>
            <strong>Notes:</strong> Merges are recorded in the <code>athleteMerges</code> collection with full snapshots of both athletes
            before the merge. To restore a merged athlete, find its <code>mergedInto</code> field in Firestore and reverse the changes manually.
          </Typography>
        </Paper>
      </Box>
    </Layout>
  );
}

export default MergeAthletes;
